import React from "react";
import { 
    IconButton, 
    Popover, 
    Box, 
    FormControlLabel, 
    Switch, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
    Typography,
    Divider,
    Tooltip
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";

export default function TimelineSettings({ 
    timelineProps, 
    onPropsChange, 
    currentTimeline 
}) {
    const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null);
    const { t } = useTranslation();
    
    const timeIntervalOptions = [
        { label: `5 ${t('time_unit_min')}`, value: 5 },
        { label: `10 ${t('time_unit_min')}`, value: 10 },
        { label: `15 ${t('time_unit_min')}`, value: 15 },
        { label: `30 ${t('time_unit_min')}`, value: 30 },
        { label: `60 ${t('time_unit_min')}`, value: 60 },
        { label: `120 ${t('time_unit_min')}`, value: 120 },
        { label: `1 ${t('time_unit_day')}`, value: 1440 },
    ];

    function handleSettingsClick(event) {
        setSettingsAnchorEl(event.currentTarget);
    }

    function handleSettingsClose() {
        setSettingsAnchorEl(null);
    }

    function handlePropChange(propName, value) {
        onPropsChange(propName, value);
    }

    const open = Boolean(settingsAnchorEl);

    return (
        <>
            <IconButton 
                onClick={handleSettingsClick}
                title="Timeline Settings"
                sx={{ 
                    color: '#808080',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                <SettingsIcon />
            </IconButton>

            <Popover
                open={open}
                anchorEl={settingsAnchorEl}
                onClose={handleSettingsClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box sx={{ p: 2, minWidth: 280 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
                        {t('timeline_settings_title')}
                    </Typography>
                    
                    {/* Time Interval */}
                    {currentTimeline.props.includes("timeIntervalMinutes") && (
                        <Tooltip title={t('timeline_tooltip_time_interval')} arrow placement="left">
                            <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
                                <InputLabel sx={{ fontSize: '0.9rem' }}>{t('timeline_settings_time_interval')}</InputLabel>
                                <Select
                                    value={timelineProps.timeIntervalMinutes}
                                    label={t('timeline_settings_time_interval')}
                                    onChange={(e) => handlePropChange("timeIntervalMinutes", e.target.value)}
                                    sx={{ 
                                        '& .MuiSelect-select': { 
                                            fontSize: '0.9rem',
                                            py: 1
                                        }
                                    }}
                                >
                                    {timeIntervalOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.9rem', py: 0.5 }}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Tooltip>
                    )}

                    {/* EMA Section */}
                    {(currentTimeline.props.includes("useEMA") || currentTimeline.props.includes("EMAPeriod")) && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            
                            {currentTimeline.props.includes("useEMA") && (
                                <Tooltip title={t('timeline_tooltip_use_ema')} arrow placement="left">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={timelineProps.useEMA}
                                                onChange={(e) => handlePropChange("useEMA", e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={t('timeline_settings_use_ema')}
                                        sx={{ 
                                            mb: 2, 
                                            display: 'flex',
                                            '& .MuiFormControlLabel-label': { fontSize: '0.9rem' }
                                        }}
                                    />
                                </Tooltip>
                            )}

                            {currentTimeline.props.includes("EMAPeriod") && timelineProps.useEMA && (
                                <Tooltip title={t('timeline_tooltip_ema_period')} arrow placement="left">
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('timeline_settings_ema_period')}
                                        value={timelineProps.EMAPeriod}
                                        onChange={(e) => handlePropChange("EMAPeriod", parseInt(e.target.value))}
                                        size="small"
                                        sx={{ 
                                            mb: 1.5,
                                            '& .MuiInputLabel-root': { fontSize: '0.9rem' },
                                            '& .MuiInputBase-input': { fontSize: '0.9rem', py: 1 }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            
                            <Divider sx={{ my: 1 }} />
                        </>
                    )}

                    {/* Other Settings */}
                    {currentTimeline.props.includes("useNormalized") && (
                        <Tooltip title={t('timeline_tooltip_use_normalized')} arrow placement="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={timelineProps.useNormalized}
                                        onChange={(e) => handlePropChange("useNormalized", e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={t('timeline_settings_use_normalized')}
                                sx={{ 
                                    mb: 1, 
                                    display: 'flex',
                                    '& .MuiFormControlLabel-label': { fontSize: '0.9rem' }
                                }}
                            />
                        </Tooltip>
                    )}

                    {currentTimeline.props.includes("showSuppliers") && (
                        <Tooltip title={t('timeline_tooltip_show_suppliers')} arrow placement="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={timelineProps.showSuppliers}
                                        onChange={(e) => handlePropChange("showSuppliers", e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={t('timeline_settings_show_suppliers')}
                                sx={{ 
                                    mb: 1, 
                                    display: 'flex',
                                    '& .MuiFormControlLabel-label': { fontSize: '0.9rem' }
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </Popover>
        </>
    );
} 